import React from 'react';
import { getViewportForWidth, Viewport } from '@att-bit/duc.hooks.use-viewport';

export enum Orientation {
    LANDSCAPE,
    PORTRAIT,
}

export interface IUseWindowSize {
    width: number,
    height: number,
    orientation: Orientation,
    desktop: boolean,
    tablet: boolean,
    mobile: boolean,
    viewport: Viewport,
}

export const useWindowSize = (userAgent?): IUseWindowSize => {

    const defaultWindowSize = React.useMemo(() => {
        let width = 1366;
        let height = 768;
        let orientation = Orientation.LANDSCAPE;
        if (userAgent?.match(/Tablet|iPad/i)) {
            width = 1024;
        } else if (userAgent?.match(/Mobile|Windows Phone|Lumia|Android|webOS|iPhone|Blackberry/i)) {
            width = 767;
            orientation = Orientation.PORTRAIT;
        }
        const viewport = getViewportForWidth(width);
        return {
            width,
            height,
            orientation,
            desktop: viewport === Viewport.DESKTOP,
            tablet: viewport === Viewport.TABLET,
            mobile: viewport === Viewport.MOBILE,
            viewport: viewport
        }
    }, [])

    const [windowSize, setWindowSize] = React.useState(defaultWindowSize);

    const getWindowDims = () => {
        const widths = [];
        const heights = [];
        window.innerWidth && widths.push(window.innerWidth);
        window.innerHeight && heights.push(window.innerHeight);
        
        if (window.screen?.width) {
            widths.push(window.screen?.width);
            heights.push(window.screen?.height);
        }
        return {
            /** in ipad 12.9 innerwidth is not getting picked */
            // width: widths.length > 0 && Math.min(...widths),
            // height: heights.length > 0 && Math.min(...heights),
            width: window.innerWidth ? window.innerWidth : widths.length > 0 && Math.min(...widths),
            height: window.innerHeight ? window.innerHeight : heights.length > 0 && Math.min(...heights),
        }
    }
  
    const getWindowSize = () => {
        const dims = getWindowDims();
        const viewport = getViewportForWidth(dims.width);
        return {
            width: dims.width,
            height: dims.height,
            desktop: viewport === Viewport.DESKTOP,
            tablet: viewport === Viewport.TABLET,
            mobile: viewport === Viewport.MOBILE,
            viewport: viewport
        }
    }

    const getWindowOrientation = () => {
        const bottom = window.innerHeight || document.documentElement.clientHeight;
        const right = window.innerWidth || document.documentElement.clientWidth;
        return {
            orientation: bottom > right ? Orientation.PORTRAIT : Orientation.LANDSCAPE,
        }
    }

    React.useEffect(() => {
        let timeoutId = null;

        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setWindowSize({
                    ...getWindowSize(),
                    ...getWindowOrientation(),
                });
            }, 100);
        }

        setWindowSize({
            ...getWindowSize(),
            ...getWindowOrientation(),
        });

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        }
    }, []);

    return windowSize;
}
